(() => {
  const cardSelector = '[data-sf-product-card]';
  const initializedCards = new WeakMap();

  class SFProductCardGallery {
    constructor(card) {
      this.card = card;
      this.gallery = card.querySelector('[data-sf-product-card-gallery]');
      this.pagination = card.querySelector('.sf-product-card__pagination');
      this.dotTrack = card.querySelector('.sf-product-card__dot-track');
      this.link = card.querySelector('[data-sf-product-card-link]');
      this.slides = Array.from(card.querySelectorAll('[data-sf-slide-index]'));
      this.dots = Array.from(card.querySelectorAll('[data-sf-dot-index]'));
      this.hasGallery = Boolean(this.gallery && this.slides.length > 1);
      this.activeIndex = 0;
      this.pointerDown = false;
      this.dragging = false;
      this.capturedPointerId = null;
      this.suppressClick = false;
      this.startX = 0;
      this.allowVideoPlayback = false;
      this.hoverFrame = null;
      this.hoverX = 0;
      this.resetTimer = null;

      this.bind();

      if (this.hasGallery) {
        this.goTo(0, true, false);
      }
    }

    bind() {
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onPointerLeave = this.handlePointerLeave.bind(this);
      this.onPointerEnter = this.handlePointerEnter.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onPaginationClick = this.handlePaginationClick.bind(this);

      if (this.hasGallery) {
        this.gallery.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.gallery.addEventListener('pointermove', this.onPointerMove, { passive: false });
        this.gallery.addEventListener('pointerup', this.onPointerUp);
        this.gallery.addEventListener('pointercancel', this.onPointerUp);
        this.card.addEventListener('pointerenter', this.onPointerEnter);
        this.card.addEventListener('pointerleave', this.onPointerLeave);
        this.card.addEventListener('keydown', this.onKeydown);

        if (this.link) {
          this.link.addEventListener('click', this.onClick, true);
          this.link.addEventListener('dragstart', this.onClick, true);
        }

        if (this.pagination) {
          this.pagination.addEventListener('click', this.onPaginationClick);
        }
      }
    }

    handlePointerDown(event) {
      if (event.button && event.button !== 0) {
        return;
      }

      if (event.target.closest('.sf-product-card__pagination')) {
        return;
      }

      this.pointerDown = true;
      this.dragging = false;
      this.startX = event.clientX;
      this.lastX = event.clientX;
      this.startTime = performance.now();
      this.clearReset();
    }

    handlePointerMove(event) {
      if (!this.pointerDown) {
        if (this.pagination && this.pagination.contains(event.target)) {
          return;
        }

        this.handleMousePosition(event);
        return;
      }

      const deltaX = event.clientX - this.startX;
      this.lastX = event.clientX;

      if (Math.abs(deltaX) > 8) {
        if (!this.dragging) {
          this.capturePointer(event);
        }

        this.dragging = true;
      }

      if (this.dragging && event.cancelable) {
        event.preventDefault();
      }
    }

    handlePointerUp(event) {
      if (!this.pointerDown) {
        return;
      }

      const deltaX = event.clientX - this.startX;
      const elapsed = Math.max(performance.now() - this.startTime, 1);
      const velocity = Math.abs(deltaX) / elapsed;
      const shouldMove = Math.abs(deltaX) > 44 || Math.abs(deltaX) > 18 && velocity > 0.42;

      this.pointerDown = false;

      if (shouldMove) {
        this.goBy(deltaX < 0 ? 1 : -1);
        this.preventNextClick();
        this.releasePointer(event);
        return;
      }

      if (this.dragging) {
        this.preventNextClick();
      }

      this.releasePointer(event);
    }

    handlePointerEnter() {
      this.clearReset();
    }

    handlePointerLeave(event) {
      if (event.pointerType !== 'mouse' || this.pointerDown) {
        return;
      }

      this.scheduleReset();
    }

    capturePointer(event) {
      if (this.capturedPointerId !== null || !this.gallery.setPointerCapture) {
        return;
      }

      this.gallery.setPointerCapture(event.pointerId);
      this.capturedPointerId = event.pointerId;
    }

    releasePointer(event) {
      const pointerId = this.capturedPointerId ?? event.pointerId;

      if (this.gallery.releasePointerCapture && this.gallery.hasPointerCapture(pointerId)) {
        this.gallery.releasePointerCapture(pointerId);
      }

      this.capturedPointerId = null;
    }

    handleMousePosition(event) {
      if (event.pointerType !== 'mouse') {
        return;
      }

      if (this.pagination && this.pagination.contains(event.target)) {
        return;
      }

      this.clearReset();
      this.hoverX = event.clientX;

      if (this.hoverFrame) {
        return;
      }

      this.hoverFrame = window.requestAnimationFrame(() => {
        const rect = this.gallery.getBoundingClientRect();
        const ratio = Math.min(Math.max((this.hoverX - rect.left) / Math.max(rect.width, 1), 0), 1);
        const index = Math.round(ratio * (this.slides.length - 1));

        this.hoverFrame = null;
        this.goTo(index);
      });
    }

    scheduleReset() {
      this.clearReset();

      if (this.hoverFrame) {
        window.cancelAnimationFrame(this.hoverFrame);
        this.hoverFrame = null;
      }

      this.resetTimer = window.setTimeout(() => {
        this.resetTimer = null;
        this.goTo(0, false, false);
      }, 250);
    }

    clearReset() {
      if (this.resetTimer) {
        window.clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }
    }

    handleClick(event) {
      if (!this.suppressClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    handlePaginationClick(event) {
      let dot = event.target.closest('[data-sf-dot-index]');

      if (!dot || !this.pagination.contains(dot) || dot.dataset.sfDotState === 'hidden') {
        dot = this.getNearestVisibleDot(event.clientX);
      }

      if (!dot) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.goTo(Number(dot.dataset.sfDotIndex));
    }

    handleSwatchChange(event) {
      const swatch = this.getSwatchOption(event.target);

      if (!swatch || !event.target.matches('[type="radio"]')) {
        return;
      }

      event.stopImmediatePropagation();
      this.updateProductLinks(swatch.dataset.sfVariantUrl);
      this.goToMediaId(swatch.dataset.sfVariantMediaId);
    }

    handleSwatchPointerOver(event) {
      const swatch = this.getSwatchOption(event.target);

      if (!swatch) {
        return;
      }

      event.stopImmediatePropagation();
      this.goToMediaId(swatch.dataset.sfVariantMediaId, false);
    }

    getSwatchOption(target) {
      if (!(target instanceof Element)) {
        return null;
      }

      return target.closest('[data-sf-swatch-option]');
    }

    updateProductLinks(url) {
      if (!url) {
        return;
      }

      this.card.querySelectorAll('[data-sf-product-card-target-link]').forEach((link) => {
        link.setAttribute('href', url);
      });
    }

    goToMediaId(mediaId, playVideo = true) {
      if (!mediaId || !this.hasGallery) {
        return;
      }

      const slideIndex = this.slides.findIndex((slide) => slide.dataset.sfMediaId === mediaId);

      if (slideIndex >= 0) {
        this.goTo(slideIndex, false, playVideo);
      }
    }

    getVisibleDots() {
      return this.dots.filter((dot) => dot.dataset.sfDotState !== 'hidden');
    }

    getNearestVisibleDot(clientX) {
      if (!Number.isFinite(clientX)) {
        return null;
      }

      return this.getVisibleDots().reduce((closestDot, dot) => {
        const rect = dot.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const distance = Math.abs(clientX - center);

        if (!closestDot || distance < closestDot.distance) {
          return { dot, distance };
        }

        return closestDot;
      }, null)?.dot ?? null;
    }

    handleKeydown(event) {
      if (event.target instanceof Element && event.target.closest('.sf-product-card__swatches')) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goBy(1);
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goBy(-1);
      }
    }

    preventNextClick() {
      this.suppressClick = true;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 180);
    }

    goBy(delta) {
      this.goTo(this.activeIndex + delta);
    }

    goTo(index, force = false, playVideo = true) {
      if (!Number.isFinite(index) || this.slides.length === 0) {
        return;
      }

      const nextIndex = (index + this.slides.length) % this.slides.length;

      if (!force && nextIndex === this.activeIndex) {
        return;
      }

      this.activeIndex = nextIndex;
      this.allowVideoPlayback = playVideo;
      this.card.dataset.sfActiveIndex = String(nextIndex);

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.dataset.sfActive = isActive ? 'true' : 'false';
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === nextIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      this.updateDots();
      this.syncVideos();
    }

    updateDots() {
      const dynamicDotThreshold = 4;
      const dynamicDotWindow = 5;
      const dynamicDotCenter = Math.floor(dynamicDotWindow / 2);
      const totalDots = this.dots.length;
      const hasDynamicDots = totalDots > dynamicDotThreshold;
      const visibleDotCount = hasDynamicDots ? dynamicDotWindow : totalDots;

      if (this.pagination) {
        this.pagination.setAttribute('data-sf-visible-dot-count', String(visibleDotCount));
        this.pagination.setAttribute('data-sf-dynamic-dots', hasDynamicDots ? 'true' : 'false');
      }

      if (this.dotTrack) {
        this.dotTrack.style.setProperty('--sf-dot-window-offset', '0');
      }

      if (!hasDynamicDots) {
        this.dots.forEach((dot) => {
          dot.dataset.sfDotState = 'visible';
          dot.removeAttribute('aria-hidden');
          dot.removeAttribute('tabindex');
        });
        return;
      }

      if (this.dotTrack) {
        this.dotTrack.style.setProperty('--sf-dot-window-offset', String(dynamicDotCenter - this.activeIndex));
      }

      this.dots.forEach((dot, dotIndex) => {
        const distanceFromActive = dotIndex - this.activeIndex;
        const absoluteDistance = Math.abs(distanceFromActive);

        if (absoluteDistance === 0) {
          dot.dataset.sfDotState = 'main';
          dot.removeAttribute('aria-hidden');
          dot.removeAttribute('tabindex');
          return;
        }

        if (absoluteDistance === 1) {
          dot.dataset.sfDotState = 'near';
          dot.removeAttribute('aria-hidden');
          dot.removeAttribute('tabindex');
          return;
        }

        if (absoluteDistance === 2) {
          dot.dataset.sfDotState = 'far';
          dot.removeAttribute('aria-hidden');
          dot.removeAttribute('tabindex');
          return;
        }

        if (absoluteDistance > dynamicDotCenter) {
          dot.dataset.sfDotState = 'hidden';
          dot.setAttribute('aria-hidden', 'true');
          dot.setAttribute('tabindex', '-1');
        }
      });
    }

    syncVideos() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.slides.forEach((slide, slideIndex) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (slideIndex !== this.activeIndex || reduceMotion || !this.allowVideoPlayback) {
            video.pause();
            return;
          }

          const playPromise = video.play();

          if (playPromise) {
            playPromise.catch(() => {});
          }
        });
      });
    }
  }

  function initSurefireProductCards(root = document) {
    const scope = root instanceof Element ? root : document;
    const cards = [];

    if (scope.matches && scope.matches(cardSelector)) {
      cards.push(scope);
    }

    cards.push(...scope.querySelectorAll(cardSelector));

    cards.forEach((card) => {
      if (!initializedCards.has(card)) {
        initializedCards.set(card, new SFProductCardGallery(card));
      }
    });
  }

  function getCardInstanceFromTarget(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    const card = target.closest(cardSelector);

    if (!card) {
      return null;
    }

    if (!initializedCards.has(card)) {
      initializedCards.set(card, new SFProductCardGallery(card));
    }

    return initializedCards.get(card);
  }

  function handleDocumentSwatchChange(event) {
    getCardInstanceFromTarget(event.target)?.handleSwatchChange(event);
  }

  function handleDocumentSwatchPointerOver(event) {
    getCardInstanceFromTarget(event.target)?.handleSwatchPointerOver(event);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSurefireProductCards(), { once: true });
  } else {
    initSurefireProductCards();
  }

  document.addEventListener('change', handleDocumentSwatchChange, true);
  document.addEventListener('pointerover', handleDocumentSwatchPointerOver, true);

  document.addEventListener('shopify:section:load', (event) => {
    initSurefireProductCards(event.target);
  });

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          initSurefireProductCards(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
